import type {
  MatchConnection,
  MatchLifecycleEvent,
  MatchState,
  Unsubscribe,
} from '@signalclash/shared';

import type { AudioBus } from './AudioBus.js';

// Adaptive in-match music. Drives the music bus from the snapshot stream so
// the track follows match-phase pressure without needing extra wire from
// `Sfx.ts` or `ClientApp`. Per `soundAssets.md §3`:
//
//   music_match_calm    — match seconds 0 … 180 (expansion phase)
//   music_match_intense — match seconds 180 … storm onset
//   music_match_intense — continues through `signal_storm` / `overtime`
//                         (`music_final_rush` is V1.5+ and not yet shipped)
//
// `jingle_victory` / `jingle_defeat` are one-shots fired by `Sfx.ts` on the
// `match_ended` lifecycle event; the MusicDirector merely stops the loop so
// the jingle isn't fighting the bed track.
//
// Idempotent throughout: `AudioBus.playMusic` is a no-op when the requested
// track is already playing, so this can observe every snapshot tick without
// thrash. The `match_ended` listener uses the same audio context the rest of
// the bus uses, so volume sliders apply uniformly.
const INTENSE_PHASE_THRESHOLD_SEC = 180;

export class MusicDirector {
  private unsubSnap: Unsubscribe | null = null;
  private unsubLife: Unsubscribe | null = null;
  private muted = false;

  constructor(private readonly bus: AudioBus) {}

  // Menu music: a single call on menu-mount; the bus's name-match guard makes
  // re-mounting cheap. `playMusic` itself awaits buffer load — fire-and-forget
  // is safe because the bus serialises play calls.
  playMenu(): void {
    if (this.muted) return;
    void this.bus.playMusic('music_menu', { loop: true, fadeMs: 600, volume: 0.7 });
  }

  // Match-start. We don't wait for the first snapshot to choose calm vs
  // intense — the very first tick already carries `matchSeconds`, but Sfx /
  // banner / HUD all kick in immediately on the first snapshot, so we let
  // `onSnapshot` pick the correct track. This call ensures a smooth fade
  // from `music_menu` to silence in case the snapshot is delayed.
  attach(connection: MatchConnection): void {
    this.detach();
    this.unsubSnap = connection.onSnapshot((snap) => this.onSnapshot(snap));
    this.unsubLife = connection.onLifecycle((e) => this.onLifecycle(e));
    // Ambient bed (wind loop) underpins the entire match. Single fire-and-
    // forget call here — the AudioBus's pending-name guard makes a follow-up
    // attach (rematch) idempotent without any extra plumbing.
    if (!this.muted) {
      void this.bus.playAmbience('ambience_wind', { fadeMs: 1500, volume: 0.4 });
    }
  }

  detach(): void {
    this.unsubSnap?.();
    this.unsubSnap = null;
    this.unsubLife?.();
    this.unsubLife = null;
    this.bus.stopAmbience({ fadeMs: 600 });
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    if (muted) {
      this.bus.stopMusic({ fadeMs: 300 });
      this.bus.stopAmbience({ fadeMs: 300 });
    }
  }

  stop(): void {
    this.bus.stopMusic({ fadeMs: 600 });
    this.bus.stopAmbience({ fadeMs: 600 });
  }

  private onSnapshot(snap: MatchState): void {
    if (this.muted) return;
    // Overtime gets its own track — distinct ticking-clock loop that signals
    // the rules have changed from the regulation-phase intensity. Phases:
    //   - regulation, first 3 minutes → music_match_calm
    //   - regulation, after 3 minutes → music_match_intense
    //   - signal_storm                → music_match_intense (no track yet)
    //   - overtime                    → music_overtime
    if (snap.phase === 'overtime') {
      void this.bus.playMusic('music_overtime', { loop: true, fadeMs: 1200 });
      return;
    }
    const seconds = snap.matchSeconds;
    if (seconds < INTENSE_PHASE_THRESHOLD_SEC && snap.phase === 'regulation') {
      void this.bus.playMusic('music_match_calm', { loop: true, fadeMs: 1200 });
    } else {
      void this.bus.playMusic('music_match_intense', { loop: true, fadeMs: 1200 });
    }
  }

  private onLifecycle(e: MatchLifecycleEvent): void {
    if (e.kind === 'match_ended') {
      // Stop the bed loop so the victory / defeat jingle (one-shot SFX) lands
      // on a clean mix. A fast fade keeps the cut from sounding abrupt against
      // the EOM modal sweep-in.
      this.bus.stopMusic({ fadeMs: 400 });
    }
  }
}
